/**
 * 认证路由守卫组件
 * 检查用户登录状态和角色权限
 *
 * 支持两种布局:
 * - 租户端: 普通租户用户
 * - 平台端: 平台管理员（仅 SaaS 模式）
 *
 * REQ-20260508-0001 #10184: 加 available_portals 校验，越权跳转到
 * /exception/portal-forbidden（与 gateway portal_guard middleware 双层防御）。
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/modules/auth';
import { getStorageItem, STORAGE_KEYS } from '@/utils/storage';

export interface AuthRouteProps {
  children: React.ReactNode;
  /** 要求的角色，如 'platform_admin' */
  requiredRole?: string;
}

/**
 * 路径前缀 → 所需 portal 的映射
 * 与后端 gateway portal_guard middleware 的规则对齐（PRD §F05）
 */
function pathRequiredPortal(pathname: string): 'admin' | 'mall' | 'platform' | null {
  if (pathname === '/admin' || pathname.startsWith('/admin/')) return 'admin';
  if (pathname === '/mall' || pathname.startsWith('/mall/')) return 'mall';
  if (pathname === '/platform' || pathname.startsWith('/platform/')) return 'platform';
  return null;
}

type AuthRouteUser = Record<string, any>;

export function getAuthRouteUser(contextUser?: AuthRouteUser | null): AuthRouteUser | undefined {
  return contextUser || getStorageItem<AuthRouteUser>(STORAGE_KEYS.USER_INFO);
}

export function getAuthRouteUserScope(user?: AuthRouteUser): string | undefined {
  return user?.user_scope || user?.userScope;
}

export function getAuthRouteAvailablePortals(user?: AuthRouteUser): string[] {
  const portals = user?.available_portals || user?.availablePortals;
  return Array.isArray(portals) ? portals : [];
}

function isPlatformAdminUser(user?: AuthRouteUser): boolean {
  const userRole = user?.role || getAuthRouteUserScope(user);
  return userRole === 'platform' || userRole === 'platform_admin' || userRole === 'super_admin';
}

export function canAccessPlatformAdmin(contextUser?: AuthRouteUser | null): boolean {
  return isPlatformAdminUser(getAuthRouteUser(contextUser));
}

const AuthRoute: React.FC<AuthRouteProps> = ({ children, requiredRole }) => {
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();

  // 检查认证状态
  const isAuth = isAuthenticated();
  const effectiveUser = getAuthRouteUser(user);

  // 如果未认证，重定向到对应登录页
  if (!isAuth) {
    const isPlatformRoute = location.pathname.startsWith('/platform');
    const loginPath = isPlatformRoute ? '/platform/login' : '/login';
    return (
      <Navigate
        to={loginPath}
        state={{ from: location }}
        replace
      />
    );
  }

  // 检查角色权限（如果指定了必需角色）
  // 开发环境跳过角色检查，方便调试
  if (requiredRole && import.meta.env.PROD) {
    if (requiredRole === 'platform_admin' && !canAccessPlatformAdmin(user)) {
      // 非平台管理员尝试访问平台管理页面，重定向到403
      return (
        <Navigate
          to="/exception/403"
          replace
        />
      );
    }
  }

  // 租户端路由：平台用户应回到平台端（非代理模式）
  if (!requiredRole) {
    const userScope = getAuthRouteUserScope(effectiveUser);
    const isImpersonating = localStorage.getItem('is_impersonating') === 'true';
    if (userScope === 'platform' && !isImpersonating) {
      return <Navigate to="/platform/dashboard" replace />;
    }
  }

  // REQ-20260508-0001 #10184: 路径前缀 vs available_portals 校验
  // 仅对带 portal 前缀的路径（/admin/* / /mall/* / /platform/*）生效
  const requiredPortal = pathRequiredPortal(location.pathname);
  if (requiredPortal) {
    const userScope = getAuthRouteUserScope(effectiveUser);
    const availablePortals = getAuthRouteAvailablePortals(effectiveUser);

    // /platform/* 已由上面的 requiredRole=platform_admin 处理，这里仅校验 admin/mall
    if (requiredPortal === 'admin' || requiredPortal === 'mall') {
      // platform 用户访问租户路径已在上面拦截，到此都是 tenant scope
      // 兼容旧 token：available_portals 为空时不阻塞（与 gateway middleware 一致）
      if (
        userScope === 'tenant' &&
        availablePortals.length > 0 &&
        !availablePortals.includes(requiredPortal)
      ) {
        return (
          <Navigate
            to="/exception/portal-forbidden"
            state={{
              path: location.pathname,
              required: requiredPortal,
              available: availablePortals,
            }}
            replace
          />
        );
      }
    }
  }

  // 认证通过，渲染子组件
  return <>{children}</>;
};

export default AuthRoute;
